import { datadogLogs as WebDatadog } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import type { ContainerComponent } from '../../common/types/Common';
import { Singleton } from '../../common/utils/Singleton';
import {
    API_URL,
    FRUITFUL_ENV,
    LLM_SERVICE_BASE_URL,
} from '../../config/config';
import type { User } from '../../user/types/User';
import type { ErrorData } from '../Logger';
import { isDatadogEnabled } from './DatadogHelper';
import type { IDatadog } from './types/Datadog';

const DATADOG_CLIENT_TOKEN = 'pub069a8d64957e4c5985564796566c7f4c';
const DATADOG_RUM_APPLICATION_ID = 'ef8e45a2-4528-493e-a549-46d265b8cf79';
const SERVICE_NAME = 'fruitful-web';

const getRumConfig = () => {
    return {
        applicationId: DATADOG_RUM_APPLICATION_ID,
        clientToken: DATADOG_CLIENT_TOKEN,
        service: SERVICE_NAME,
        env: FRUITFUL_ENV,
        allowedTracingUrls: [
            {
                match: API_URL,
                propagatorTypes: ['datadog' as const],
            },
            {
                match: LLM_SERVICE_BASE_URL,
                propagatorTypes: ['datadog' as const],
            },
        ],
        traceContextInjection: 'sampled' as const,
    };
};

const getLoggingConfig = () => {
    return {
        clientToken: DATADOG_CLIENT_TOKEN,
        service: SERVICE_NAME,
        env: FRUITFUL_ENV,
    };
};

class DatadogFacade extends Singleton implements IDatadog {
    public readonly Datadog: typeof WebDatadog.logger;

    constructor() {
        super();
        this.Datadog = WebDatadog.logger;
    }

    public init(): void {
        if (!this.isEnabled()) {
            return;
        }

        datadogRum.init(getRumConfig()); // DD RUM: https://docs.datadoghq.com/integrations/rum_react/
        WebDatadog.init(getLoggingConfig()); // DD Logs: https://docs.datadoghq.com/logs/log_collection/javascript/
    }

    public identifyUser(user: User): void {
        if (!this.isEnabled()) {
            return;
        }

        datadogRum.setUser({
            id: user.id,
            email: user.email,
            visitorId: user.visitorId, // For connecting events and sessions before/after user creation
            isVisitor: false, // Only true before user account creation
        });
    }

    public identifyVisitor(visitorId: string): void {
        if (!this.isEnabled()) {
            return;
        }

        datadogRum.setUser({
            id: visitorId,
            visitorId,
            isVisitor: true,
        });
    }

    public clearUser(): void {
        if (!this.isEnabled()) {
            return;
        }

        datadogRum.clearUser();
    }

    public logError(exception: Error, data?: ErrorData) {
        if (!this.isEnabled()) {
            return;
        }

        datadogRum.addError(exception, data);
        this.Datadog.error(exception.message, data, exception);
    }

    public addAction(
        name: string,
        context?: Record<string, string | number | boolean>,
    ): void {
        if (!this.isEnabled()) {
            return;
        }

        datadogRum.addAction(name, context);
    }

    public isEnabled(): boolean {
        return isDatadogEnabled();
    }
}

export default DatadogFacade.getInstance();

export const DatadogProvider: ContainerComponent = ({ children }) => {
    return <>{children}</>;
};
